
import { createTheme, responsiveFontSizes } from '@mui/material/styles';

const arabicFontStack = [
  '"Droid Arabic Kufi"',
  '"IBM Plex Sans Arabic"',
  '"Alexandria"',
  '"Segoe UI"',
  'Tahoma',
  'sans-serif',
].join(', ');

const latinFontStack = [
  '"Segoe UI"',
  '"Roboto"',
  '"Helvetica"',
  '"Arial"',
  'sans-serif',
].join(', ');

// منع عجلة الماوس من تغيير قيم حقول الأرقام
const preventNumberWheelChange = (event) => {
  const target = event.target;

  if (
    target instanceof HTMLInputElement &&
    target.type === 'number'
  ) {
    event.preventDefault();
    event.stopPropagation();
    target.blur();
  }
};

export const buildAppTheme = (isRTL = true) => {
  const baseFontFamily = isRTL
    ? arabicFontStack
    : latinFontStack;

  let theme = createTheme({
    direction: isRTL ? 'rtl' : 'ltr',

    palette: {
      mode: 'light',

      primary: {
        main: '#4F46E5',
        light: '#818CF8',
        dark: '#3730A3',
        contrastText: '#FFFFFF',
      },

      secondary: {
        main: '#64748B',
        light: '#94A3B8',
        dark: '#475569',
        contrastText: '#FFFFFF',
      },

      success: {
        main: '#16A34A',
        light: '#4ADE80',
        dark: '#15803D',
        contrastText: '#FFFFFF',
      },

      error: {
        main: '#DC2626',
        light: '#F87171',
        dark: '#B91C1C',
        contrastText: '#FFFFFF',
      },

      warning: {
        main: '#D97706',
        light: '#FBBF24',
        dark: '#B45309',
        contrastText: '#FFFFFF',
      },

      info: {
        main: '#2563EB',
        light: '#60A5FA',
        dark: '#1D4ED8',
        contrastText: '#FFFFFF',
      },

      background: {
        default: '#F1F5F9',
        paper: '#FFFFFF',
      },

      text: {
        primary: '#0F172A',
        secondary: '#64748B',
      },

      divider: '#E2E8F0',
    },

    shape: {
      borderRadius: 12,
    },

    typography: {
      fontFamily: baseFontFamily,
      fontSize: 14,

      fontWeightLight: 400,
      fontWeightRegular: 500,
      fontWeightMedium: 600,
      fontWeightBold: 700,

      h1: {
        fontFamily: baseFontFamily,
        fontWeight: 800,
        fontSize: '2.35rem',
        lineHeight: 1.25,
      },

      h2: {
        fontFamily: baseFontFamily,
        fontWeight: 800,
        fontSize: '2rem',
        lineHeight: 1.28,
      },

      h3: {
        fontFamily: baseFontFamily,
        fontWeight: 800,
        fontSize: '1.8rem',
        lineHeight: 1.3,
      },

      h4: {
        fontFamily: baseFontFamily,
        fontWeight: 800,
        fontSize: '1.6rem',
        lineHeight: 1.32,
        letterSpacing: '-0.3px',
      },

      h5: {
        fontFamily: baseFontFamily,
        fontWeight: 800,
        fontSize: '1.38rem',
        lineHeight: 1.34,
      },

      h6: {
        fontFamily: baseFontFamily,
        fontWeight: 800,
        fontSize: '1.18rem',
        lineHeight: 1.36,
      },

      subtitle1: {
        fontFamily: baseFontFamily,
        fontWeight: 600,
        fontSize: '0.95rem',
        lineHeight: 1.6,
      },

      subtitle2: {
        fontFamily: baseFontFamily,
        fontWeight: 600,
        fontSize: '0.875rem',
        lineHeight: 1.55,
      },

      body1: {
        fontFamily: baseFontFamily,
        fontWeight: 500,
        fontSize: '0.95rem',
        lineHeight: 1.7,
      },

      body2: {
        fontFamily: baseFontFamily,
        fontWeight: 500,
        fontSize: '0.875rem',
        lineHeight: 1.65,
      },

      button: {
        fontFamily: baseFontFamily,
        fontWeight: 700,
        fontSize: '0.875rem',
        textTransform: 'none',
        lineHeight: 1.4,
      },

      caption: {
        fontFamily: baseFontFamily,
        fontWeight: 500,
        fontSize: '0.78rem',
        lineHeight: 1.45,
      },

      overline: {
        fontFamily: baseFontFamily,
        fontWeight: 700,
        fontSize: '0.75rem',
        lineHeight: 1.45,
        textTransform: 'none',
      },
    },

    components: {
      // الخط والخلفية العامة
      MuiCssBaseline: {
        styleOverrides: {
          'html, body, #root': {
            fontFamily: `${baseFontFamily} !important`,
            backgroundColor: '#F1F5F9',
            minHeight: '100%',
          },

          '*, *::before, *::after': {
            boxSizing: 'border-box',
          },

          body: {
            fontFamily: `${baseFontFamily} !important`,
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: 1.65,
            margin: 0,
          },

          'input, button, select, textarea, label, span, div, p, a, li':
            {
              fontFamily: `${baseFontFamily} !important`,
            },
        },
      },

      // العناوين والنصوص
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            subtitle1: 'p',
            subtitle2: 'p',
          },
        },

        styleOverrides: {
          root: {
            fontFamily: baseFontFamily,
          },
        },
      },

      MuiButtonBase: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
          },
        },
      },

      // جميع الأزرار: حجم وشكل موحدان
      MuiButton: {
        defaultProps: {
          variant: 'contained',
          size: 'small',
          disableElevation: true,
        },

        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 700,
            fontSize: '0.84rem',
            textTransform: 'none',

            minHeight: 36,
            minWidth: 0,
            padding: '7px 14px',
            gap: 6,

            borderRadius: '9px',
            boxShadow: 'none',

            transition:
              'background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease',

            '& .MuiButton-startIcon': {
              marginInlineStart: 0,
              marginInlineEnd: 4,
            },

            '& .MuiButton-endIcon': {
              marginInlineStart: 4,
              marginInlineEnd: 0,
            },

            '&:hover': {
              boxShadow: '0 3px 8px rgba(15, 23, 42, 0.12)',
              transform: 'none',
            },

            '&:active': {
              boxShadow: 'none',
              transform: 'none',
            },

            '&.Mui-disabled': {
              boxShadow: 'none',
              opacity: 0.55,
            },
          },

          sizeSmall: {
            minHeight: 32,
            padding: '5px 11px',
            fontSize: '0.8rem',
            borderRadius: '8px',
          },

          sizeMedium: {
            minHeight: 38,
            padding: '8px 16px',
            fontSize: '0.875rem',
          },

          sizeLarge: {
            minHeight: 44,
            padding: '10px 20px',
            fontSize: '0.95rem',
          },

          contained: {
            boxShadow: 'none',

            '&:hover': {
              boxShadow: '0 3px 8px rgba(15, 23, 42, 0.12)',
            },
          },

          outlined: {
            borderWidth: '1px',
            boxShadow: 'none',

            '&:hover': {
              borderWidth: '1px',
              boxShadow: 'none',
            },
          },

          text: {
            boxShadow: 'none',

            '&:hover': {
              boxShadow: 'none',
            },
          },
        },
      },

      // إعدادات الحقول النصية
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
          onWheel: preventNumberWheelChange,
        },
      },

      // منع عجلة الماوس في مكونات الإدخال التابعة لـ MUI
      MuiInputBase: {
        defaultProps: {
          onWheel: preventNumberWheelChange,
        },

        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 500,
            fontSize: '0.9rem',
          },

          input: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 500,
            fontSize: '0.9rem',
            minWidth: 0,
          },
        },
      },

      // تصميم موحد للحقول
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            backgroundColor: '#FFFFFF',
            fontFamily: `${baseFontFamily} !important`,
            transition: 'border-color 0.18s ease, box-shadow 0.18s ease',

            '& fieldset': {
              borderColor: '#CBD5E1',
            },

            '&:hover fieldset': {
              borderColor: '#94A3B8',
            },

            '&.Mui-focused': {
              backgroundColor: '#FFFFFF',
              boxShadow: '0 0 0 3px rgba(79, 70, 229, 0.10)',
            },

            '&.Mui-focused fieldset': {
              borderColor: '#4F46E5',
              borderWidth: '1px',
            },

            '&.Mui-error fieldset': {
              borderColor: '#DC2626',
            },
          },

          input: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 500,
            fontSize: '0.9rem',
            paddingTop: '9px',
            paddingBottom: '9px',
          },

          inputSizeSmall: {
            paddingTop: '7px',
            paddingBottom: '7px',
          },
        },
      },

      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 600,
            fontSize: '0.86rem',

            '&.Mui-focused': {
              color: '#4F46E5',
            },
          },
        },
      },

      MuiFormHelperText: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 500,
            fontSize: '0.78rem',
          },
        },
      },

      MuiFormControlLabel: {
        styleOverrides: {
          label: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 600,
            fontSize: '0.875rem',
          },
        },
      },

      MuiCheckbox: {
        styleOverrides: {
          root: {
            padding: 6,
            borderRadius: 6,
          },
        },
      },

      MuiRadio: {
        styleOverrides: {
          root: {
            padding: 6,
          },
        },
      },

      MuiSwitch: {
        styleOverrides: {
          root: {
            padding: 8,
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 500,
            fontSize: '0.875rem',
            minHeight: 38,
          },
        },
      },

      MuiSelect: {
  styleOverrides: {
    root: {
      minWidth: 100,
    },
    select: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
},

      MuiChip: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 600,
            fontSize: '0.8rem',
            borderRadius: 8,
          },

          label: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 600,
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontSize: '0.875rem',
            fontWeight: 500,
            borderColor: '#E2E8F0',
          },

          head: {
            fontWeight: 700,
            fontSize: '0.875rem',
            color: '#334155',
            backgroundColor: '#F8FAFC',
          },
        },
      },

      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:last-child td, &:last-child th': {
              borderBottom: 0,
            },
          },
        },
      },

      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '16px',
            boxShadow: '0 4px 18px rgba(15, 23, 42, 0.05)',
            border: '1px solid #E2E8F0',
            backgroundImage: 'none',
          },
        },
      },

      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },

          rounded: {
            borderRadius: '12px',
          },
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 800,
            fontSize: '1.1rem',
          },
        },
      },

      MuiDialogContent: {
        styleOverrides: {
          root: {
            paddingTop: '16px',
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'none',
            minHeight: 44,
          },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontFamily: `${baseFontFamily} !important`,
            fontSize: '0.78rem',
            borderRadius: 6,
          },
        },
      },

      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontFamily: `${baseFontFamily} !important`,
          },

          message: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 500,
          },
        },
      },

      MuiDataGrid: {
        styleOverrides: {
          root: {
            fontFamily: `${baseFontFamily} !important`,
            fontSize: '0.875rem',
            borderColor: '#E2E8F0',
            borderRadius: 12,
          },

          cell: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 500,
          },

          columnHeader: {
            backgroundColor: '#F8FAFC',
          },

          columnHeaderTitle: {
            fontFamily: `${baseFontFamily} !important`,
            fontWeight: 700,
            fontSize: '0.875rem',
          },

          toolbarContainer: {
            fontFamily: `${baseFontFamily} !important`,
            fontSize: '0.875rem',
          },

          footerContainer: {
            fontFamily: `${baseFontFamily} !important`,
            fontSize: '0.875rem',
          },
        },
      },
    },
  });

  theme = responsiveFontSizes(theme, {
    breakpoints: ['sm', 'md', 'lg'],
    factor: 2,
  });

  return theme;
};

export default buildAppTheme(true);
